import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import NewChatList from './NewChatList';
import NewChatWindow from './NewChatWindow';

interface NewChatLayoutProps {
  selectedChatId: string | null;
  selectedChat: any;
  selectedEmployee: any;
  newChat: boolean;
  isLoading: boolean;
  isChatLoading: boolean;
  messages: any[];
  userMap: Record<string, any>;
  chatList: any[];
  unreadCounts: Record<string, number>;
  uploadingImages: boolean;
  isTyping: boolean;
  showGallery: boolean;
  callActive: boolean;
  myStream: MediaStream | null;
  remoteStream: MediaStream | null;
  callAccepted: boolean;
  mute: boolean;
  incomingCall: boolean;
  callId: string;
  isVideoEnabled: boolean;
  isRemoteVideoEnabled: boolean;
  showNotes: boolean;
  notes: any[];
  isMinimized: boolean;
  isMaximized: boolean;
  activeChatTab: string;
  page: number;
  hasMore: boolean;
  isFetching: boolean;
  replyToId: string | null;

  // [OK] callbacks
  onSelectChat: (chatId: string, chat: any) => void;
  onSendMessage: (message: string, attachments: any) => void;
  onTyping: () => void;

  // Keep this for future (viewport read), but NewMessageList no longer spams it.
  onMessagesRead: (messageIds: string[]) => void;

  onReply: (messageId: string) => void;
  onClearReply: () => void;
  onStartCall: (shouldStart: boolean) => void;

  // [OK] NEW: load more history
  loadMoreMessages: () => void;

  // setters (kept same as your current signature)
  setCallActive: (active: boolean) => void;
  setMute: (mute: boolean) => void;
  setIncomingCall: (incoming: boolean) => void;
  setCallId: (id: string) => void;
  setIsVideoEnabled: (enabled: boolean) => void;
  setIsRemoteVideoEnabled: (enabled: boolean) => void;
  setShowNotes: (show: boolean) => void;
  setNotes: (notes: any[]) => void;
  setIsMinimized: (minimized: boolean) => void;
  setIsMaximized: (maximized: boolean) => void;
  setActiveChatTab: (tab: string) => void;
  setPage: (page: number) => void;
  setShowGallery: (show: boolean) => void;
  userUID?: string;
  currentUID?: string;
}

const NewChatLayout: React.FC<NewChatLayoutProps> = ({
  selectedChatId,
  selectedChat,
  chatList,
  isLoading,
  messages,
  userMap,
  unreadCounts,
  uploadingImages,
  isTyping,
  page,
  hasMore,
  isFetching,
  replyToId,
  onSelectChat,
  onSendMessage,
  onTyping,
  onMessagesRead,
  onReply,
  onClearReply,
  onStartCall,
  loadMoreMessages,
}) => {
  const { t } = useTranslation();
  const [showChatList, setShowChatList] = useState(true);

  return (
    <div className="flex h-full w-full overflow-hidden bg-background-light dark:bg-background-dark">
      {/* Chat List */}
      <AnimatePresence>
        {showChatList && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full md:w-80 lg:w-96 border-r border-gray-200 dark:border-gray-800 flex flex-col"
          >
            <NewChatList
              chatList={chatList}
              selectedChat={selectedChatId}
              onChatSelect={onSelectChat}
              unreadMessages={unreadCounts}
              isLoading={isLoading}
              setIsLoading={() => {}}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      {selectedChatId && selectedChat ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <NewChatWindow
            messages={messages}
            selectedChat={selectedChat}
            onSendMessage={onSendMessage}
            chatId={selectedChatId}
            isLoading={false}
            loadMoreMessages={loadMoreMessages} // [OK] real paging
            hasMore={hasMore}
            isFetchingMore={isFetching}
            page={page}
            uploadingImages={uploadingImages}
            isTyping={isTyping}
            onOpenGallery={() => {}}
            handleStartCall={onStartCall}
            onBack={() => setShowChatList(true)}
            setShowChatList={setShowChatList}
            showChatList={showChatList}
            chatList={chatList}
            userMap={userMap}
            onMessagesRead={onMessagesRead}
            onReply={onReply}
            replyToId={replyToId}
            clearReply={onClearReply}
            onTyping={onTyping}
          />
        </motion.div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {isLoading ? t('Loading chats...') : t('Select a chat to start messaging')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewChatLayout;
